import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ReactDatePicker from 'react-datepicker';
import Select, { ValueType } from 'react-select';
import moment from 'moment';

import Button, {
  ButtonSize,
  ButtonType
} from '../shared/components/core-ui/button/button.component';
import { birdToast } from '../shared/components/core-ui/toast';
import { Pagination } from '../shared/components/core-ui/pagination/pagination.component';
import { isErrorInfo } from '../shared/interfaces/error-info.interface';
import {
  LandlordDetailedReportSearchModel,
  LandlordDetailedReportTotalsModel,
  LandlordReportFilterOptionsModel,
  LandlordReportMode,
  LandlordReportNodeModel
} from '../shared/models/landlord-detailed-report.model';
import { LoadingData } from '../ui/LoadingData';
import { NotFoundData } from '../ui/NotFoundData';
import {
  getLandlordDetailedReport,
  getLandlordReportFilterOptions
} from './landlord-detailed-report.service';

const selectMenuPortalStyles = {
  menuPortal: (base: any) => ({ ...base, zIndex: 9999 })
};

const EMPTY_TOTALS: LandlordDetailedReportTotalsModel = {
  totalRentGeneratedPerUnit: 0,
  amountDueToLandlord: 0,
  expenses: 0,
  llAmount: 0
};

const MODES: { value: LandlordReportMode; label: string }[] = [
  { value: 'list', label: 'List' },
  { value: 'landlord', label: 'Landlord Aggregated' },
  { value: 'area', label: 'Area Aggregated' }
];

/** List mode keeps the full per-reservation detail; the aggregated modes are the money columns. */
const LIST_COLUMNS = 22;
const AGGREGATED_COLUMNS = 8;

/**
 * Only used when the account column is deliberately hidden — see the toggle above the filters.
 * Masking style matches the landlord bank-details card so the two read the same.
 */
const maskAccountNumber = (value: string): string => {
  if (!value) return '—';
  const last4 = value.slice(-4);
  const masked = '•'.repeat(Math.max(value.length - 4, 0)) + last4;
  return (masked.match(/.{1,4}/g) || [masked]).join(' ');
};

const EMPTY_OPTIONS: LandlordReportFilterOptionsModel = {
  landlords: [],
  areas: [],
  properties: [],
  unitTypes: [],
  unitTypeGroups: [],
  rooms: []
};

const formatMoney = (value?: number | null) => {
  if (value === null || value === undefined) return '—';
  return new Intl.NumberFormat('en-GB', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value);
};

const formatDate = (ms?: number) => {
  if (!ms) return '—';
  return moment(ms).format('DD MMM YYYY');
};

type StringOption = { value: string; label: string };
type NumberOption = { value: number; label: string };

type NodeRowProps = {
  node: LandlordReportNodeModel;
  depth: number;
  expanded: Set<string>;
  onToggle: (key: string) => void;
  revealAccounts: boolean;
};

/** The three payout columns, rendered identically in both tables. */
type ResolvedBank =
  | { status: 'one'; account: NonNullable<LandlordReportNodeModel['bankAccount']> }
  | { status: 'multiple' }
  | { status: 'none' };

/**
 * The account to show on a row that is not itself a unit.
 *
 * Bank details hang off units, because a landlord *name* can cover many landlord records — 'Sahel'
 * is 206 of them — and there is no single account for such a group. But most landlords are not
 * Sahel: where every unit underneath a row resolves to the same account, that row can show it, and
 * finance can pay from the landlord line without drilling in. Where they differ, saying so is more
 * useful than a blank.
 *
 * The whole tree is in the payload regardless of what is expanded, so this is derived here rather
 * than adding a rolled-up field to the API.
 */
const resolveBankAccount = (node: LandlordReportNodeModel): ResolvedBank => {
  const accounts = new Map<string, NonNullable<LandlordReportNodeModel['bankAccount']>>();
  const walk = (current: LandlordReportNodeModel) => {
    if (current.bankAccount && current.bankAccount.accountNumber) {
      accounts.set(current.bankAccount.accountNumber, current.bankAccount);
    }
    (current.children || []).forEach(walk);
  };
  walk(node);

  const found = Array.from(accounts.values());
  if (found.length === 1) return { status: 'one', account: found[0] };
  if (found.length > 1) return { status: 'multiple' };
  return { status: 'none' };
};

const BankCells = ({
  node,
  revealAccounts
}: {
  node: LandlordReportNodeModel;
  revealAccounts: boolean;
}) => {
  const resolved = useMemo(() => resolveBankAccount(node), [node]);

  // A reservation or an expense is already under the unit that carries the account; repeating it
  // on every child would just be noise.
  if (node.kind === 'reservation' || node.kind === 'expense') {
    return (
      <>
        <td>—</td>
        <td>—</td>
        <td>—</td>
      </>
    );
  }

  if (resolved.status === 'multiple') {
    return (
      <>
        <td>—</td>
        <td>—</td>
        <td className="whitespace-nowrap text-neutral-400">Multiple accounts</td>
      </>
    );
  }

  if (resolved.status === 'none') {
    return (
      <>
        <td>—</td>
        <td>—</td>
        <td className="whitespace-nowrap text-neutral-400">No bank details</td>
      </>
    );
  }

  const bank = resolved.account;
  return (
    <>
      <td>{bank.bankName || '—'}</td>
      <td>{bank.accountHolderName || '—'}</td>
      <td className="whitespace-nowrap font-gilroy-regular">
        {revealAccounts ? bank.accountNumber : maskAccountNumber(bank.accountNumber)}
      </td>
    </>
  );
};

/** Chevron gutter. Leaves get a spacer so every row's first real column lines up. */
const Expander = ({
  node,
  depth,
  isOpen,
  onToggle
}: {
  node: LandlordReportNodeModel;
  depth: number;
  isOpen: boolean;
  onToggle: (key: string) => void;
}) => {
  const hasChildren = Boolean(node.children && node.children.length);
  return (
    <td className="border-t border-gray-100 px-2 py-3 align-middle whitespace-nowrap">
      <span style={{ paddingLeft: depth * 18 }} className="inline-flex items-center">
        {hasChildren ? (
          <button
            type="button"
            aria-expanded={isOpen}
            aria-label={isOpen ? `Collapse ${node.label}` : `Expand ${node.label}`}
            onClick={() => onToggle(node.key)}
            className="mr-1 flex h-6 w-6 items-center justify-center rounded text-neutral-500 hover:bg-primary-50 hover:text-primary-600"
          >
            {isOpen ? '−' : '+'}
          </button>
        ) : (
          <span className="mr-1 inline-block h-6 w-6" />
        )}
      </span>
    </td>
  );
};

const KIND_LABEL: Record<LandlordReportNodeModel['kind'], string> = {
  area: 'Area',
  landlord: 'Landlord',
  unit: 'Unit',
  reservation: 'Reservation',
  expense: 'Expense'
};

/**
 * The aggregated tree: label, then the three money columns the payout workbook uses.
 * Recursive, and rendered only when its parent is open, which is what keeps a deep tree cheap.
 */
const AggregatedRows = ({ node, depth, expanded, onToggle, revealAccounts }: NodeRowProps) => {
  const isOpen = expanded.has(node.key);
  const isExpense = node.kind === 'expense';
  return (
    <>
      <tr
        className={`transition-colors hover:bg-creams-50 [&>td]:border-t [&>td]:border-gray-100 [&>td]:px-4 [&>td]:py-3 [&>td]:text-lg ${
          node.kind === 'area' || node.kind === 'landlord'
            ? '[&>td]:font-gilroy-bold [&>td]:text-neutral-900'
            : '[&>td]:font-gilroy-regular [&>td]:text-neutral-500'
        }`}
      >
        <Expander node={node} depth={depth} isOpen={isOpen} onToggle={onToggle} />
        <td>
          <span className="mr-2 text-sm uppercase tracking-wider text-neutral-400">
            {KIND_LABEL[node.kind]}
          </span>
          {node.label}
          {isExpense && node.expense ? (
            <span className="ml-2 text-base text-neutral-400">
              {node.expense.category} · {formatDate(node.expense.paymentDate)}
            </span>
          ) : null}
        </td>
        <td className="text-right">{formatMoney(node.amountDueToLandlord)}</td>
        <td className={`text-right ${node.expenses ? 'text-rose-600' : ''}`}>
          {node.expenses ? formatMoney(node.expenses) : '—'}
        </td>
        <td className="text-right">{formatMoney(node.llAmount)}</td>
        <BankCells node={node} revealAccounts={revealAccounts} />
      </tr>
      {isOpen && node.children
        ? node.children.map((child) => (
            <AggregatedRows
              key={child.key}
              node={child}
              depth={depth + 1}
              expanded={expanded}
              onToggle={onToggle}
              revealAccounts={revealAccounts}
            />
          ))
        : null}
    </>
  );
};

const AggregatedTable = ({
  nodes,
  totals,
  mode,
  expanded,
  onToggle,
  revealAccounts
}: {
  nodes: LandlordReportNodeModel[];
  totals: LandlordDetailedReportTotalsModel;
  mode: LandlordReportMode;
  expanded: Set<string>;
  onToggle: (key: string) => void;
  revealAccounts: boolean;
}) => (
  <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
    <div className="w-full overflow-x-auto">
      <table
        className="min-w-[1500px] w-full border-separate border-spacing-0"
        aria-label={mode === 'area' ? 'Area aggregated report' : 'Landlord aggregated report'}
      >
        <thead className="sticky top-0 z-10 bg-white">
          <tr className="border-b border-gray-200 text-neutral-900 [&>th]:py-4 [&>th]:font-gilroy-bold [&>th]:text-lg [&>th]:font-medium [&>th]:uppercase [&>th]:tracking-[0.12em]">
            <th className="px-2" scope="col" aria-label="Expand" />
            <th className="px-4 text-left whitespace-nowrap" scope="col">
              {mode === 'area' ? 'Area / Landlord / Unit' : 'Landlord / Unit'}
            </th>
            <th className="px-4 text-right whitespace-nowrap" scope="col">
              Amount Due to Landlord
            </th>
            <th className="px-4 text-right whitespace-nowrap" scope="col">
              Expenses
            </th>
            <th className="px-4 text-right whitespace-nowrap" scope="col">
              LL Amount
            </th>
            <th className="px-4 text-left whitespace-nowrap" scope="col">
              Bank
            </th>
            <th className="px-4 text-left whitespace-nowrap" scope="col">
              Account Holder
            </th>
            <th className="px-4 text-left whitespace-nowrap" scope="col">
              Account Number
            </th>
          </tr>
        </thead>
        <tbody>
          {nodes.map((node) => (
            <AggregatedRows
              key={node.key}
              node={node}
              depth={0}
              expanded={expanded}
              onToggle={onToggle}
              revealAccounts={revealAccounts}
            />
          ))}
        </tbody>
        <tfoot className="sticky bottom-0 bg-white">
          <tr className="[&>td]:border-t-2 [&>td]:border-gray-300 [&>td]:px-4 [&>td]:py-4 [&>td]:text-xl [&>td]:font-gilroy-bold [&>td]:text-neutral-900">
            <td colSpan={AGGREGATED_COLUMNS - 6} className="text-left uppercase tracking-[0.12em]">
              Grand total
            </td>
            <td className="text-right">{formatMoney(totals.amountDueToLandlord)}</td>
            <td className="text-right text-rose-600">{formatMoney(totals.expenses)}</td>
            <td className="text-right">{formatMoney(totals.llAmount)}</td>
            <td colSpan={3} />
          </tr>
        </tfoot>
      </table>
    </div>
  </div>
);

/**
 * List mode: unit rows carrying the subtotal, expanding to their reservations and expense lines.
 * Grouped by unit rather than flat because expenses belong to a unit, and a unit can have several
 * reservations in one window — hanging them off each reservation would count them more than once.
 */
const ListRows = ({ node, depth, expanded, onToggle, revealAccounts }: NodeRowProps) => {
  const isOpen = expanded.has(node.key);
  const r = node.reservation;
  const isUnit = node.kind === 'unit';
  return (
    <>
      <tr
        className={`transition-colors hover:bg-creams-50 [&>td]:border-t [&>td]:border-gray-100 [&>td]:px-4 [&>td]:py-3 [&>td]:text-lg ${
          isUnit
            ? '[&>td]:font-gilroy-bold [&>td]:text-neutral-900'
            : '[&>td]:font-gilroy-regular [&>td]:text-neutral-500'
        }`}
      >
        <Expander node={node} depth={depth} isOpen={isOpen} onToggle={onToggle} />
        <td className="whitespace-nowrap">
          <span className="mr-2 text-sm uppercase tracking-wider text-neutral-400">
            {KIND_LABEL[node.kind]}
          </span>
          {node.label}
        </td>
        <td>{r ? r.landlordId || '—' : node.landlord?.landlordId || '—'}</td>
        <td>{r ? r.landlordName || '—' : node.landlord?.landlordName || '—'}</td>
        <td>{r ? r.landlordPhone || '—' : node.landlord?.landlordPhone || '—'}</td>
        <td>{r ? r.landlordEmail || '—' : node.landlord?.landlordEmail || '—'}</td>
        <td>{r ? r.propertyName || '—' : node.unit?.propertyName || '—'}</td>
        <td>{r ? r.unitId : node.unit?.unitId ?? '—'}</td>
        <td>{r ? r.unitName || '—' : node.unit?.unitName || '—'}</td>
        <td>{r ? r.unitShortName || '—' : node.unit?.unitShortName || '—'}</td>
        <td>{r ? r.unitTypeGroupName || '—' : node.unit?.unitTypeGroupName || '—'}</td>
        <td>{r ? r.areaId || '—' : '—'}</td>
        <td>{r ? r.areaName || '—' : node.unit?.areaName || '—'}</td>
        <td className="text-right">{r ? r.bedrooms ?? '—' : node.unit?.bedrooms ?? '—'}</td>
        <td className="text-right">{formatMoney(node.totalRentGeneratedPerUnit)}</td>
        <td className="text-right">{formatMoney(node.amountDueToLandlord)}</td>
        <td className={`text-right ${node.expenses ? 'text-rose-600' : ''}`}>
          {node.expenses ? formatMoney(node.expenses) : '—'}
        </td>
        <td className="text-right">{formatMoney(node.llAmount)}</td>
        <td className="whitespace-nowrap">
          {node.kind === 'expense' && node.expense
            ? `${node.expense.category} · ${formatDate(node.expense.paymentDate)}`
            : r
            ? `${formatDate(r.checkInDate)} → ${formatDate(r.checkOutDate)}`
            : '—'}
        </td>
        <BankCells node={node} revealAccounts={revealAccounts} />
      </tr>
      {isOpen && node.children
        ? node.children.map((child) => (
            <ListRows
              key={child.key}
              node={child}
              depth={depth + 1}
              expanded={expanded}
              onToggle={onToggle}
              revealAccounts={revealAccounts}
            />
          ))
        : null}
    </>
  );
};

const ListTable = ({
  nodes,
  totals,
  count,
  expanded,
  onToggle,
  revealAccounts
}: {
  nodes: LandlordReportNodeModel[];
  totals: LandlordDetailedReportTotalsModel;
  count: number;
  expanded: Set<string>;
  onToggle: (key: string) => void;
  revealAccounts: boolean;
}) => (
  <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
    <div className="w-full overflow-x-auto">
      <table
        className="min-w-[3200px] w-full border-separate border-spacing-0"
        aria-label="Landlord detailed report"
      >
        <thead className="sticky top-0 z-10 bg-white">
          <tr className="border-b border-gray-200 text-neutral-900 [&>th]:py-4 [&>th]:font-gilroy-bold [&>th]:text-lg [&>th]:font-medium [&>th]:uppercase [&>th]:tracking-[0.12em]">
            <th className="px-2" scope="col" aria-label="Expand" />
            <th className="px-4 text-left whitespace-nowrap" scope="col">
              Unit / Row
            </th>
            <th className="px-4 text-left whitespace-nowrap" scope="col">
              Landlord ID
            </th>
            <th className="px-4 text-left whitespace-nowrap" scope="col">
              Landlord Name
            </th>
            <th className="px-4 text-left whitespace-nowrap" scope="col">
              Landlord Phone
            </th>
            <th className="px-4 text-left whitespace-nowrap" scope="col">
              Landlord Email
            </th>
            <th className="px-4 text-left whitespace-nowrap" scope="col">
              Property Name
            </th>
            <th className="px-4 text-left whitespace-nowrap" scope="col">
              Unit ID
            </th>
            <th className="px-4 text-left whitespace-nowrap" scope="col">
              Unit Name
            </th>
            <th className="px-4 text-left whitespace-nowrap" scope="col">
              Unit Short Name
            </th>
            <th className="px-4 text-left whitespace-nowrap" scope="col">
              Unit Type Group
            </th>
            <th className="px-4 text-left whitespace-nowrap" scope="col">
              Area
            </th>
            <th className="px-4 text-left whitespace-nowrap" scope="col">
              Area Name
            </th>
            <th className="px-4 text-right whitespace-nowrap" scope="col">
              Bedrooms
            </th>
            <th className="px-4 text-right whitespace-nowrap" scope="col">
              Total Rent Generated
            </th>
            <th className="px-4 text-right whitespace-nowrap" scope="col">
              Amount Due to Landlord
            </th>
            <th className="px-4 text-right whitespace-nowrap" scope="col">
              Expenses
            </th>
            <th className="px-4 text-right whitespace-nowrap" scope="col">
              LL Amount
            </th>
            <th className="px-4 text-left whitespace-nowrap" scope="col">
              Dates
            </th>
            <th className="px-4 text-left whitespace-nowrap" scope="col">
              Bank
            </th>
            <th className="px-4 text-left whitespace-nowrap" scope="col">
              Account Holder
            </th>
            <th className="px-4 text-left whitespace-nowrap" scope="col">
              Account Number
            </th>
          </tr>
        </thead>
        <tbody>
          {nodes.map((node) => (
            <ListRows
              key={node.key}
              node={node}
              depth={0}
              expanded={expanded}
              onToggle={onToggle}
              revealAccounts={revealAccounts}
            />
          ))}
        </tbody>
        {/* Totals span the whole filtered set, not this page, so paging never changes them. */}
        <tfoot className="sticky bottom-0 bg-white">
          <tr className="[&>td]:border-t-2 [&>td]:border-gray-300 [&>td]:px-4 [&>td]:py-4 [&>td]:text-xl [&>td]:font-gilroy-bold [&>td]:text-neutral-900">
            <td colSpan={LIST_COLUMNS - 8} className="text-left uppercase tracking-[0.12em]">
              Totals — all {count} units
            </td>
            <td className="text-right">{formatMoney(totals.totalRentGeneratedPerUnit)}</td>
            <td className="text-right">{formatMoney(totals.amountDueToLandlord)}</td>
            <td className="text-right text-rose-600">{formatMoney(totals.expenses)}</td>
            <td className="text-right">{formatMoney(totals.llAmount)}</td>
            <td colSpan={4} />
          </tr>
        </tfoot>
      </table>
    </div>
  </div>
);

const LandlordDetailedReportPage = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const [rows, setRows] = useState<LandlordReportNodeModel[]>([]);
  const [mode, setMode] = useState<LandlordReportMode>('list');
  // One flat set of node keys across every level — the keys are path-derived so they cannot collide.
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  // Shown in full by default. This report exists to get transfers raised, and an account number
  // you have to un-hide before you can copy it is friction on the one task the page is for. The
  // report is already gated to finance. The toggle stays as a way to hide the column when the
  // screen is being shared.
  const [revealAccounts, setRevealAccounts] = useState(true);
  const [listCount, setListCount] = useState(0);
  const [totals, setTotals] = useState<LandlordDetailedReportTotalsModel>(EMPTY_TOTALS);
  const [truncated, setTruncated] = useState(false);

  const [offset, setOffset] = useState(1);
  const [limit, setLimit] = useState(20);

  const [checkInDate, setCheckInDate] = useState<Date>(moment().startOf('month').toDate());
  // The window's end date is an inclusive night, so a whole month is the 1st to the 31st.
  // The window reads like a stay — check-out is not a charged night — so a whole month runs
  // 1st to 1st.
  const [checkOutDate, setCheckOutDate] = useState<Date>(
    moment().add(1, 'month').startOf('month').toDate()
  );

  const [landlordKey, setLandlordKey] = useState('');
  const [areaId, setAreaId] = useState('');
  const [propertyId, setPropertyId] = useState('');
  const [unitTypeId, setUnitTypeId] = useState<number | undefined>(undefined);
  const [unitTypeGroupId, setUnitTypeGroupId] = useState('');
  const [roomId, setRoomId] = useState('');

  const [options, setOptions] = useState<LandlordReportFilterOptionsModel>(EMPTY_OPTIONS);

  useEffect(() => {
    getLandlordReportFilterOptions().then((response) => {
      if (!isErrorInfo(response)) {
        setOptions(response);
      } else {
        birdToast.error('Filters could not be loaded', 'Landlord detailed report');
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The dropdowns cascade in the order they are displayed: each one narrows every dropdown after
  // it. Landlord is last and therefore the most constrained — by the time you reach it, it only
  // offers landlords who actually own something in the current selection.
  //
  // Each stage is the set of unit types still reachable, and every dropdown is derived from the
  // stage above it. Deriving from unit types (rather than from the flat lists) is what lets a
  // later filter constrain an earlier dimension.
  const byArea = useMemo(
    () => options.unitTypes.filter((unitType) => !areaId || unitType.areaId === areaId),
    [areaId, options.unitTypes]
  );

  const byProperty = useMemo(
    () => byArea.filter((unitType) => !propertyId || unitType.propertyId === propertyId),
    [byArea, propertyId]
  );

  const byUnit = useMemo(
    () => byProperty.filter((unitType) => !unitTypeId || unitType.id === unitTypeId),
    [byProperty, unitTypeId]
  );

  const byGroup = useMemo(
    () =>
      byUnit.filter((unitType) => !unitTypeGroupId || unitType.unitTypeGroupId === unitTypeGroupId),
    [byUnit, unitTypeGroupId]
  );

  const byRoom = useMemo(() => {
    if (!roomId) return byGroup;
    const owner = options.rooms.find((room) => room.roomId === roomId);
    return owner ? byGroup.filter((unitType) => unitType.id === owner.unitTypeId) : byGroup;
  }, [byGroup, options.rooms, roomId]);

  // Name lookups, so each dropdown can label an id without carrying the name around.
  const areaNames = useMemo(
    () => new Map(options.areas.map((area) => [area.id, area.name])),
    [options.areas]
  );
  const propertyNames = useMemo(
    () => new Map(options.properties.map((property) => [property.id, property.name])),
    [options.properties]
  );
  const groupNames = useMemo(
    () => new Map(options.unitTypeGroups.map((group) => [group.id, group.name])),
    [options.unitTypeGroups]
  );
  // options.landlords is keyed by case-folded name, not landlordId — one name covers many landlord
  // records, and keying by id would list 200+ entries all reading the same name.
  const landlordNames = useMemo(
    () => new Map(options.landlords.map((landlord) => [landlord.id, landlord.name])),
    [options.landlords]
  );

  const distinct = (
    unitTypes: LandlordReportFilterOptionsModel['unitTypes'],
    key: 'areaId' | 'propertyId' | 'unitTypeGroupId' | 'landlordKey',
    names: Map<string, string>
  ): StringOption[] => {
    const seen = new Map<string, string>();
    unitTypes.forEach((unitType) => {
      const id = unitType[key];
      if (id) seen.set(id, names.get(id) || id);
    });
    return Array.from(seen, ([value, label]) => ({ value, label })).sort((first, second) =>
      first.label.localeCompare(second.label)
    );
  };

  const areaOptions: StringOption[] = useMemo(
    () => distinct(options.unitTypes, 'areaId', areaNames),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [areaNames, options.unitTypes]
  );

  const propertyOptions: StringOption[] = useMemo(
    // eslint-disable-next-line react-hooks/exhaustive-deps
    () => distinct(byArea, 'propertyId', propertyNames),
    [byArea, propertyNames]
  );

  const unitTypeOptions: NumberOption[] = useMemo(() => {
    const seen = new Map<number, string>();
    byProperty.forEach((unitType) => seen.set(unitType.id, unitType.name || String(unitType.id)));
    return Array.from(seen, ([value, label]) => ({ value, label })).sort((first, second) =>
      first.label.localeCompare(second.label)
    );
  }, [byProperty]);

  const unitTypeGroupOptions: StringOption[] = useMemo(
    // eslint-disable-next-line react-hooks/exhaustive-deps
    () => distinct(byUnit, 'unitTypeGroupId', groupNames),
    [byUnit, groupNames]
  );

  const roomOptions: StringOption[] = useMemo(() => {
    const reachable = new Set(byGroup.map((unitType) => unitType.id));
    return options.rooms
      .filter((room) => reachable.has(room.unitTypeId))
      .map((room) => ({ value: room.roomId, label: room.shortName }))
      .sort((first, second) => first.label.localeCompare(second.label));
  }, [byGroup, options.rooms]);

  // Last in the chain, so it sees every other selection.
  const landlordOptions: StringOption[] = useMemo(
    // eslint-disable-next-line react-hooks/exhaustive-deps
    () => distinct(byRoom, 'landlordKey', landlordNames),
    [byRoom, landlordNames]
  );

  // Narrowing an earlier filter can strand a later selection that is no longer offered. Clearing
  // it here rather than in each onChange means the rule holds however the selection was reached,
  // including the filter-options response arriving after a choice was made.
  useEffect(() => {
    if (propertyId && !propertyOptions.some((option) => option.value === propertyId)) {
      setPropertyId('');
    }
  }, [propertyId, propertyOptions]);

  useEffect(() => {
    if (unitTypeId && !unitTypeOptions.some((option) => option.value === unitTypeId)) {
      setUnitTypeId(undefined);
    }
  }, [unitTypeId, unitTypeOptions]);

  useEffect(() => {
    if (
      unitTypeGroupId &&
      !unitTypeGroupOptions.some((option) => option.value === unitTypeGroupId)
    ) {
      setUnitTypeGroupId('');
    }
  }, [unitTypeGroupId, unitTypeGroupOptions]);

  useEffect(() => {
    if (roomId && !roomOptions.some((option) => option.value === roomId)) {
      setRoomId('');
    }
  }, [roomId, roomOptions]);

  useEffect(() => {
    if (landlordKey && !landlordOptions.some((option) => option.value === landlordKey)) {
      setLandlordKey('');
    }
  }, [landlordKey, landlordOptions]);

  const loadReportWithMode = useCallback(
    (reportMode: LandlordReportMode, page: number, perPage: number) => {
      setLoading(true);
      setError(false);

      const search: LandlordDetailedReportSearchModel = {
        checkInDate: moment(checkInDate).startOf('day').valueOf(),
        checkOutDate: moment(checkOutDate).startOf('day').valueOf(),
        offset: page,
        limit: perPage,
        mode: reportMode,
        query: {
          ...(landlordKey ? { landlordName: landlordKey } : {}),
          ...(areaId ? { areaId } : {}),
          ...(propertyId ? { propertyId } : {}),
          ...(unitTypeId ? { unitTypeId } : {}),
          ...(unitTypeGroupId ? { unitTypeGroupId } : {}),
          ...(roomId ? { roomId } : {})
        }
      };

      getLandlordDetailedReport(search).then((response) => {
        setLoading(false);
        setHasSearched(true);

        if (isErrorInfo(response)) {
          setError(true);
          setRows([]);
          setListCount(0);
          setTotals(EMPTY_TOTALS);
          setTruncated(false);
          return;
        }

        setRows(response.rows);
        setListCount(response.count);
        setTotals(response.totals);
        setTruncated(response.truncated);
      });
    },
    [
      areaId,
      checkInDate,
      checkOutDate,
      landlordKey,
      propertyId,
      roomId,
      unitTypeGroupId,
      unitTypeId
    ]
  );

  const loadReport = useCallback(
    (page: number, perPage: number) => loadReportWithMode(mode, page, perPage),
    [loadReportWithMode, mode]
  );

  // Stated in the UI rather than left to be inferred from the numbers: which nights this window
  // actually counts. The convention has been misread more than once, and every time it was because
  // nothing on screen said what the dates meant.
  const windowSummary = useMemo(() => {
    const from = moment(checkInDate).startOf('day');
    const to = moment(checkOutDate).startOf('day');
    const nights = to.diff(from, 'days');
    if (nights <= 0) {
      return null;
    }
    return {
      nights,
      firstNight: from.format('D MMM'),
      lastNight: to.clone().subtract(1, 'day').format('D MMM YYYY')
    };
  }, [checkInDate, checkOutDate]);

  const handleGenerate = () => {
    if (!checkInDate || !checkOutDate) {
      birdToast.error('Pick both a check-in and a check-out date', 'Landlord detailed report');
      return;
    }

    // Both window dates are inclusive, so an equal pair is a legitimate single-night window.
    // Check-out is not a charged night, so an equal pair is an empty window that would report
    // nothing rather than erroring.
    if (!moment(checkOutDate).isAfter(moment(checkInDate), 'day')) {
      birdToast.error(
        'Check-out must be after check-in — the check-out day itself is not charged',
        'Landlord detailed report'
      );
      return;
    }

    setOffset(1);
    loadReport(1, limit);
  };

  const handleClearFilters = () => {
    setLandlordKey('');
    setAreaId('');
    setPropertyId('');
    setUnitTypeId(undefined);
    setUnitTypeGroupId('');
    setRoomId('');
  };

  const toggleNode = (key: string) => {
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const handleModeChange = (next: LandlordReportMode) => {
    if (next === mode) {
      return;
    }
    setMode(next);
    // Expanded keys are mode-specific, and the shapes differ, so a stale set would leave rows open
    // that no longer exist. The report refetches because the server does the grouping.
    setExpanded(new Set());
    setOffset(1);
    if (hasSearched) {
      loadReportWithMode(next, 1, limit);
    }
  };

  const handlePageChange = (newPage: number) => {
    setOffset(newPage);
    loadReport(newPage, limit);
  };

  const handleItemsPerPage = (itemsPerPage: number) => {
    setLimit(itemsPerPage);
    setOffset(1);
    loadReport(1, itemsPerPage);
  };

  return (
    <div className="min-h-screen bg-creams-50 px-6 py-4">
      <section className="sticky top-0 z-30 -mx-6 mb-6 flex flex-col gap-4 border-b border-gray-200 bg-creams-50 px-6 py-4">
        <div>
          <h1 className="text-4xl font-recoleta font-semibold text-neutral-500">
            Landlord Detailed Report
          </h1>
          <p className="mt-1 text-base font-gilroy-medium text-neutral-400">
            Rent generated and amount due per landlord unit, pro-rated across the nights below. A
            check-out day is never charged — the guest's own, or the window's — so a whole month
            runs 1st to 1st.
          </p>
        </div>

        {/* Three buttons rather than the shared tabs component: that one owns its own active state
            and renders the panel body, both of which fight one dataset shown three ways. Classes are
            lifted from it so this still reads as the same design system. */}
        <div role="tablist" aria-label="Report view" className="flex flex-wrap gap-2">
          {MODES.map((option) => {
            const isActive = option.value === mode;
            return (
              <button
                key={option.value}
                type="button"
                role="tab"
                aria-selected={isActive}
                onClick={() => handleModeChange(option.value)}
                className={`rounded-xl px-4 py-2 text-base font-gilroy-medium transition-colors ${
                  isActive
                    ? 'bg-primary-600 text-white shadow-elevation-1'
                    : 'text-primary-700 hover:bg-primary-50'
                }`}
              >
                {option.label}
              </button>
            );
          })}
          <button
            type="button"
            aria-pressed={revealAccounts}
            onClick={() => setRevealAccounts((current) => !current)}
            className="ml-auto rounded-xl px-4 py-2 text-base font-gilroy-medium text-primary-700 transition-colors hover:bg-primary-50"
          >
            {revealAccounts ? 'Hide account numbers' : 'Show account numbers'}
          </button>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <div>
            <label className="mb-1 block text-sm font-gilroy-medium text-neutral-400">Area</label>
            <Select
              menuPortalTarget={document.body}
              menuPosition="fixed"
              styles={selectMenuPortalStyles}
              options={areaOptions}
              placeholder="All areas"
              value={areaOptions.find((option) => option.value === areaId) ?? null}
              isClearable
              classNamePrefix="react-select"
              onChange={(value: ValueType<StringOption, false>) => {
                setAreaId(value?.value || '');
                setPropertyId('');
                setUnitTypeId(undefined);
                setRoomId('');
              }}
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-gilroy-medium text-neutral-400">
              Property
            </label>
            <Select
              menuPortalTarget={document.body}
              menuPosition="fixed"
              styles={selectMenuPortalStyles}
              options={propertyOptions}
              placeholder="All properties"
              value={propertyOptions.find((option) => option.value === propertyId) ?? null}
              isClearable
              classNamePrefix="react-select"
              onChange={(value: ValueType<StringOption, false>) => {
                setPropertyId(value?.value || '');
                setUnitTypeId(undefined);
                setRoomId('');
              }}
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-gilroy-medium text-neutral-400">Unit</label>
            <Select
              menuPortalTarget={document.body}
              menuPosition="fixed"
              styles={selectMenuPortalStyles}
              options={unitTypeOptions}
              placeholder="All units"
              value={unitTypeOptions.find((option) => option.value === unitTypeId) ?? null}
              isClearable
              classNamePrefix="react-select"
              onChange={(value: ValueType<NumberOption, false>) => {
                setUnitTypeId(value?.value ?? undefined);
                setRoomId('');
              }}
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-gilroy-medium text-neutral-400">
              Unit Type Group
            </label>
            <Select
              menuPortalTarget={document.body}
              menuPosition="fixed"
              styles={selectMenuPortalStyles}
              options={unitTypeGroupOptions}
              placeholder="All unit type groups"
              value={
                unitTypeGroupOptions.find((option) => option.value === unitTypeGroupId) ?? null
              }
              isClearable
              classNamePrefix="react-select"
              onChange={(value: ValueType<StringOption, false>) =>
                setUnitTypeGroupId(value?.value || '')
              }
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-gilroy-medium text-neutral-400">
              Unit Short Name
            </label>
            <Select
              menuPortalTarget={document.body}
              menuPosition="fixed"
              styles={selectMenuPortalStyles}
              options={roomOptions}
              placeholder="All short names"
              value={roomOptions.find((option) => option.value === roomId) ?? null}
              isClearable
              classNamePrefix="react-select"
              onChange={(value: ValueType<StringOption, false>) => setRoomId(value?.value || '')}
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-gilroy-medium text-neutral-400">
              Landlord
            </label>
            <Select
              menuPortalTarget={document.body}
              menuPosition="fixed"
              styles={selectMenuPortalStyles}
              options={landlordOptions}
              placeholder={
                landlordOptions.length ? 'All landlords' : 'No landlords match the filters'
              }
              value={landlordOptions.find((option) => option.value === landlordKey) ?? null}
              isClearable
              classNamePrefix="react-select"
              onChange={(value: ValueType<StringOption, false>) =>
                setLandlordKey(value?.value || '')
              }
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-gilroy-medium text-neutral-400">
              Check-in Date
            </label>
            <ReactDatePicker
              selected={checkInDate}
              onChange={(date: Date | null) => {
                if (!date) return;
                setCheckInDate(date);
                if (checkOutDate.getTime() < date.getTime()) setCheckOutDate(date);
              }}
              placeholderText="From"
              className="w-full h-[38px] px-3 rounded border border-gray-300 text-sm"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-gilroy-medium text-neutral-400">
              Check-out Date
            </label>
            <ReactDatePicker
              selected={checkOutDate}
              minDate={checkInDate}
              onChange={(date: Date | null) => {
                if (!date) return;
                setCheckOutDate(date);
              }}
              placeholderText="To"
              className="w-full h-[38px] px-3 rounded border border-gray-300 text-sm"
            />
          </div>
        </div>

        <p className="text-base font-gilroy-medium text-neutral-500">
          {windowSummary
            ? `Counting ${windowSummary.nights} ${
                windowSummary.nights === 1 ? 'night' : 'nights'
              } — ${windowSummary.firstNight} to ${windowSummary.lastNight}`
            : 'Check-out must be after check-in — the check-out day itself is not charged'}
        </p>

        <div className="flex flex-wrap items-center gap-3">
          <Button
            onClick={handleGenerate}
            type={ButtonType.primary}
            size={ButtonSize.small}
            classNameButton="px-5 py-3 text-sm leading-4"
          >
            Generate
          </Button>
          <Button
            onClick={handleClearFilters}
            type={ButtonType.primaryInverted}
            size={ButtonSize.small}
            classNameButton="px-5 py-3 text-sm leading-4"
          >
            Clear filters
          </Button>
        </div>
      </section>

      <section className="mt-6 space-y-6">
        {truncated && (
          <div className="rounded-2xl border border-amber-200 bg-amber-50 px-5 py-4">
            <p className="text-lg font-gilroy-medium text-amber-700">
              This window returned more reservations than the report can total accurately. Narrow
              the date range or add a filter — the figures below are incomplete.
            </p>
          </div>
        )}

        {loading ? (
          <div className="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
            <LoadingData />
          </div>
        ) : error ? (
          <div className="rounded-3xl border border-gray-200 bg-white p-8 shadow-sm">
            <p className="text-base font-gilroy-bold uppercase tracking-[0.22em] text-rose-500">
              Finance
            </p>
            <h2 className="mt-3 text-4xl font-recoleta text-neutral-500">
              Unable to load the landlord detailed report
            </h2>
            <p className="mt-3 max-w-xl text-lg font-gilroy-medium text-neutral-400">
              The report could not be generated. Retry, or narrow the date range if the window is
              very wide.
            </p>
            <Button
              onClick={() => loadReport(offset, limit)}
              type={ButtonType.primary}
              size={ButtonSize.small}
              className="mt-6"
              classNameButton="px-5 py-3 text-sm leading-4"
            >
              Retry
            </Button>
          </div>
        ) : !hasSearched ? (
          <div className="rounded-3xl border border-gray-200 bg-white p-10 text-center shadow-sm">
            <h2 className="text-3xl font-recoleta text-neutral-500">
              Choose a window and hit Generate
            </h2>
            <p className="mt-3 text-lg font-gilroy-medium text-neutral-400">
              Rent is pro-rated across the dates you pick, so the report always needs a check-in and
              a check-out date. Expand a unit to see its reservations and the expenses deducted from
              its payout.
            </p>
          </div>
        ) : rows.length === 0 ? (
          <div className="rounded-3xl border border-gray-200 bg-white p-10 shadow-sm">
            <NotFoundData />
          </div>
        ) : (
          <>
            {mode === 'list' ? (
              <ListTable
                nodes={rows}
                totals={totals}
                count={listCount}
                expanded={expanded}
                onToggle={toggleNode}
                revealAccounts={revealAccounts}
              />
            ) : (
              <AggregatedTable
                nodes={rows}
                totals={totals}
                mode={mode}
                expanded={expanded}
                onToggle={toggleNode}
                revealAccounts={revealAccounts}
              />
            )}

            {/* Paging runs over top-level nodes. The aggregated modes are tens of rows and come
                back whole, so a pager there would only be able to split a landlord from its units. */}
            {mode === 'list' && listCount > 0 && (
              <div className="flex items-center justify-center pb-4">
                <Pagination
                  itemCounts={listCount}
                  itemsPerPage={limit}
                  offset={offset}
                  handlePageChange={handlePageChange}
                  handleItemsPerPage={handleItemsPerPage}
                />
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
};

export default LandlordDetailedReportPage;
